/* eslint-disable max-len */
/* eslint-disable no-bitwise */
import { crc16 } from 'ngx-modbus';
import {
  ReturnDistributeCmd,
  ReturnDistributeCmdDescription,
  ReturnDownloadCmd,
  ReturnDownloadCmdDescription,
  ReturnQueryFirmwareBitMap,
  ReturnQueryFirmwareBitMapDescription,
  ReturnValidateCmd,
  ReturnValidateCmdDescription,
  SgpCommandCode,
  SgpFlagMode,
  SgpMasterDeviceType,
} from './general.model';
import {
  CommandFieldParams,
  ResponseDetails,
  SgpResponse,
  SgpResponseDistributeFirmware,
  SgpResponseGetFirmwareVersion,
  SgpResponseHandshake,
  SgpResponseQueryFirmwareBitMap,
  SgpResponseStartFirmwareDownload,
  SgpResponseValidateFirmware,
} from './response.model';
import { SgpSupport } from './sgp-support.model';

export class SgpResponseDecoder {

  /**
   * Data Frame Structure
   *  + Normal Mode
   *    - [flagField, validationField, commandField, lengthIndicationField, infoField]
   *  + Advanced Mode
   *    - [flagField, validationField, controlWord, commandField, lengthIndicationField, addressField, infoField]
   *
   * Fields
   * - flagField: 2 byte
   * - validationField: 2 byte
   * - controlWord: 2 byte
   * - commandField: 2 byte
   * - lengthIndicationField: 2 byte
   * - addressField: x byte [x = 0 ~ 7] (default 1 byte)
   * - infoField: N byte
   *
   * @Note
   * lengthIndicationField => length of infoField + (length of addressField if flagField is Advanced)
   */
  static DecodeResponse(data: Uint8Array) {
    const response: Partial<ResponseDetails> = {};
    response.flagField = SgpSupport.GetFlagMode(data.slice(0, 2));
    response.validationField = {
      raw: data.slice(2, 4),
    };

    if (response.flagField === SgpFlagMode.Advanced) {
      response.flagField = SgpFlagMode.Advanced;
      response.controlWordField = {
        raw: data.slice(4, 6),
        value: SgpSupport.GetControlWordParams(data.slice(4, 6)),
      };
      response.commandField = {
        raw: data.slice(6, 8),
        value: SgpSupport.GetCommandField(data.slice(6, 8)),
      };
      response.lengthIndicatorField = {
        value: SgpSupport.GetLengthIndicationField(data.slice(8, 10)),
        raw: data.slice(8, 10),
      };
      const addressFieldLength = response.controlWordField.value.addressLength;
      response.addressField = {
        value: SgpSupport.GetLengthIndicationField(data.slice(10, 10 + addressFieldLength)),
        raw: data.slice(10, 11),
      };

      const lengthIndicationField = response.lengthIndicatorField.value;
      const infoFieldRaw = data.slice(10 + addressFieldLength, 10 + addressFieldLength + lengthIndicationField);
      response.infoField = {
        raw: infoFieldRaw,
        value: SgpResponseDecoder.DecodeInfoField(infoFieldRaw, response.commandField.value),
      };
    } else if (response.flagField === SgpFlagMode.Normal) {
      response.commandField = {
        raw: data.slice(4, 6),
        value: SgpSupport.GetCommandField(data.slice(4, 6)),
      };
      response.lengthIndicatorField = {
        value: SgpSupport.GetLengthIndicationField(data.slice(6, 8)),
        raw: data.slice(6, 8),
      };
      const lengthIndicationField = response.lengthIndicatorField.value;
      const infoFieldRaw = data.slice(8, 8 + lengthIndicationField);
      response.infoField = {
        raw: infoFieldRaw,
        value: SgpResponseDecoder.DecodeInfoField(infoFieldRaw, response.commandField.value),
      };
    }

    /**
     * Validation field (FCS)
     *
     * @Note
     * fcs = crc8 => (control word) + command field + length indication field + (address field) + information field.
     */
    const isAdvancedMode = response.flagField === SgpFlagMode.Advanced;
    const { highByte, lowByte } = crc16(new Uint8Array([
      ...(isAdvancedMode ? response.controlWordField?.raw || [] : []),
      ...(response.commandField?.raw || []),
      ...(response.lengthIndicatorField?.raw || []),
      ...(isAdvancedMode ? response.addressField?.raw || [] : []),
      ...(response.infoField?.raw || []),
    ]));

    response.validationField.isValid = [lowByte, highByte].every(
      (v, i) => response.validationField?.raw && v === response.validationField.raw[i],
    );

    return response;
  }

  static DecodeInfoField(data: Uint8Array, commandField: CommandFieldParams): SgpResponse {
    let infoField: SgpResponse;
    switch (commandField.commandCode) {
      case SgpCommandCode.Handshake:
        infoField = SgpResponseDecoder.DecodeHandshakeResponse(data);
        break;
      case SgpCommandCode.GetFirmwareVersion:
        infoField = SgpResponseDecoder.DecodeGetFirmwareVersionResponse(data);
        break;
      case SgpCommandCode.StartFirmwareDownload:
        infoField = SgpResponseDecoder.DecodeStartFirmwareDownloadResponse(data);
        break;
      case SgpCommandCode.DistributeFirmware:
        infoField = SgpResponseDecoder.DecodeDistributeFirmwareResponse(data);
        break;
      case SgpCommandCode.QueryFirmwareBitMap:
        infoField = SgpResponseDecoder.DecodeQueryFirmwareBitMapResponse(data);
        break;
      case SgpCommandCode.ValidateFirmware:
        infoField = SgpResponseDecoder.DecodeValidateFirmwareResponse(data);
        break;

      default:
        throw new Error('Not implemented');
    }

    return infoField;
  }

  /**
   *
   * Format the information field
   * Protocol version (1 Byte) | Size of single buffer (2 Bytes) | Device type (1 Byte)
   *  -  Protocol version, BCD code, default V0.1
   *  -  Size of single buffer, Return the biggest size of a single buffer
   *  -  Device type, 0 PC, 1 upper computer
   *
   * @Note
   *   Device is cell phone -  upper computer
   */
  static DecodeHandshakeResponse(data: Uint8Array): SgpResponseHandshake {
    return {
      commandResponseType: SgpCommandCode.Handshake,
      protocolVersion: data[0],
      maxSizeOfSingleBuffer: data.slice(1, 3).reverse().reduce((a, b) => (a << 8) + b),
      deviceSlaveSupports: data[3] as SgpMasterDeviceType,
    };
  }

  /**
   * Format the information field
   * Device type (32 Bytes)
   */
  static DecodeGetFirmwareVersionResponse(data: Uint8Array): SgpResponseGetFirmwareVersion {
    return {
      commandResponseType: SgpCommandCode.GetFirmwareVersion,
      deviceType: SgpSupport.ConvertToString(data),
    };
  }

  /**
   * Format the information field
   * ReturnCode (1 Byte) | Resume at breakpoint - starting address (4 Bytes)
   */
  static DecodeStartFirmwareDownloadResponse(data: Uint8Array): SgpResponseStartFirmwareDownload {
    const returnCode = data[0] as ReturnDownloadCmd;
    return {
      commandResponseType: SgpCommandCode.StartFirmwareDownload,
      returnCode,
      description: ReturnDownloadCmdDescription[returnCode],
      // if returnCode = ReturnDownloadCmd.Normal -> startAddress = 0
      startAddress: returnCode === ReturnDownloadCmd.Normal ? 0 : data.slice(1, 5).reverse().reduce((a, b) => (a << 8) + b),
    };
  }

  /**
   * Format the information field
   * ReturnCode (1 Byte)
   */
  static DecodeDistributeFirmwareResponse(data: Uint8Array): SgpResponseDistributeFirmware {
    const returnCode = data[0] as ReturnDistributeCmd;
    return {
      commandResponseType: SgpCommandCode.DistributeFirmware,
      returnCode,
      description: ReturnDistributeCmdDescription[returnCode],
    };
  }

  /**
   * Format the information field
   * ReturnCode (1 Byte)
   */
  static DecodeValidateFirmwareResponse(data: Uint8Array): SgpResponseValidateFirmware {
    const returnCode = data[0] as ReturnValidateCmd;
    return {
      commandResponseType: SgpCommandCode.ValidateFirmware,
      returnCode,
      description: ReturnValidateCmdDescription[returnCode],
    };
  }

  /**
   * Format the information field
   * ReturnCode (1 Byte) | Query bitmap offset (2 Bytes) | Query number of bitmap (2 Bytes) | Bitmap (n Bytes)
   */
  static DecodeQueryFirmwareBitMapResponse(data: Uint8Array): SgpResponseQueryFirmwareBitMap {
    const returnCode = data[0] as ReturnQueryFirmwareBitMap;
    return {
      commandResponseType: SgpCommandCode.QueryFirmwareBitMap,
      returnCode,
      description: ReturnQueryFirmwareBitMapDescription[returnCode],
      queryOffsetOfBitMap: data.slice(1, 3).reduce((a, b) => (a << 8) + b),
      queryNumberOfBitMap: data.slice(3, 5).reduce((a, b) => (a << 8) + b),
      bitmap: data.slice(5),
      failedPacket: SgpSupport.GetFailedPackets(data.slice(5)),
    };
  }
}
